import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { signOut } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { CreditsCard } from '@/components/ui/credits-card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { NotificationsMenu } from '@/components/ui/notifications-menu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';
import {
  LayoutDashboard,
  Plus,
  LogOut,
  User,
  Menu,
  X,
  Shield,
  MessageSquare,
  Users,
} from 'lucide-react';

import logoImg from '@/logo.png';
import logoWhiteImg from '@/logo-white.png';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData } = useAuth();
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const currentLogo = theme === 'dark' ? logoWhiteImg : logoImg;

  useEffect(() => {
    // Check if user is admin
    const checkAdminStatus = async () => {
      try {
        const userData = await api.get<{ role: string }>('/api/users/me');
        setIsAdmin(userData.role === 'admin');
      } catch (error) {
        console.error('Failed to check admin status:', error);
      }
    };

    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
  };

  const menuItems = [
    { icon: LayoutDashboard, label: t('nav.dashboard', 'Dashboard'), path: '/' },
    { icon: Plus, label: t('nav.newGeneration', 'New Generation'), path: '/new' },
  ];

  const adminMenuItems = [
    { icon: Users, label: 'Usuários', path: '/admin/users' },
    { icon: MessageSquare, label: 'Feedbacks', path: '/admin/feedbacks' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Header - Mobile Only */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </button>

          {/* Logo - Centered */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <img src={currentLogo} alt="Cataloga" className="h-14 w-auto" />
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-0.5 shrink-0">
            <NotificationsMenu />
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Top Header - Desktop Only */}
      <header className="hidden md:block fixed top-0 left-64 right-0 z-40 h-16 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="h-full px-8 flex items-center justify-end gap-3">
          <NotificationsMenu />
          <ThemeToggle />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 overflow-y-auto flex flex-col z-40 transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 md:left-0
      `}>
        {/* Close button for mobile */}
        <div className="md:hidden flex justify-end p-4 border-b dark:border-gray-700">
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Logo - Desktop only */}
        <div className="hidden md:flex justify-center p-2 border-b dark:border-gray-700">
          <img src={currentLogo} alt="Cataloga" className="h-20 w-auto" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </button>
            );
          })}

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className="pt-6 pb-2">
                <div className="flex items-center gap-2 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <Shield className="h-4 w-4" />
                  Administração
                </div>
              </div>
              {adminMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </button>
                );
              })}
            </>
          )}

          {/* Credits Card */}
          <div className="pt-4">
            <CreditsCard />
          </div>
        </nav>

        {/* User Info + Logout at bottom */}
        <div className="p-4 border-t dark:border-gray-700 space-y-2">
          {/* User Info */}
          <div className="flex items-center gap-3 px-4 py-2">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="" className="h-8 w-8 rounded-full flex-shrink-0" referrerPolicy="no-referrer" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{userData?.name || user?.displayName || user?.email}</p>
              {(userData?.name || user?.displayName) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
              )}
            </div>
          </div>

          {/* Logout Button */}
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={handleSignOut}
          >
            <LogOut className="h-5 w-5 mr-3" />
            {t('auth.logout', 'Logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-16 md:pt-16 md:pl-64">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
